import { type FC, type ReactNode, useMemo, useState } from "react";
import { ArrowRight, Loader2, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { CategoryEditDialog } from "@/shared/components/EstateItems/CategoryEditDialog";
import { SUPPORTED_CATEGORIES } from "@/shared/components/EstateItems/categoryEditHelpers";
import { EditEstateItemModal } from "@/shared/components/EstateItems/EditEstateItemModal";
import type { EstateItem } from "@/shared/types/estateTypes";

import { AccountRow } from "./AccountRow";
import { buildItemSubtitle } from "./itemSubtitles";

export interface ReviewSection {
  label: string;
  items: EstateItem[];
  showEmpty?: boolean;
  onAdd?: () => void;
  addLabel?: string;
}

interface ModuleReviewLayoutProps {
  sections: ReviewSection[];
  isLoading: boolean;
  onComplete: () => void;
  renderItem?: (item: EstateItem, defaultRow: ReactNode) => ReactNode;
  onAdd?: () => void;
  addLabel?: string;
  showAddButton?: boolean;
  buttonLabel?: string;
  estateId?: number | null;
  updateItem: (id: number, data: Record<string, unknown>) => Promise<unknown>;
  isUpdatingItem: boolean;
  deleteItem: (id: number) => Promise<unknown>;
  isDeletingItem: boolean;
  children?: ReactNode;
}

export const ModuleReviewLayout: FC<ModuleReviewLayoutProps> = ({
  sections,
  isLoading,
  onComplete,
  renderItem,
  onAdd,
  addLabel = "Add another",
  showAddButton = false,
  buttonLabel = "Looks good",
  estateId,
  updateItem,
  isUpdatingItem,
  deleteItem,
  isDeletingItem,
  children,
}) => {
  const [editItem, setEditItem] = useState<EstateItem | null>(null);
  const totalItems = useMemo(
    () => sections.reduce((sum, s) => sum + s.items.length, 0),
    [sections],
  );

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-2xl items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (totalItems === 0 && !children) {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        <div className="rounded-lg border border-primary bg-white p-6">
          <EmptyState
            title="No items added yet"
            actionLabel={onAdd ? addLabel : undefined}
            onAction={onAdd}
          />
        </div>
        <div className="mt-4 flex flex-col items-center">
          <Button variant="brand" rounded onClick={onComplete} className="w-full max-w-[400px]">
            {buttonLabel} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <div className="flex flex-col gap-6 rounded-lg border border-primary bg-white p-6">
        {sections.map((section) => {
          const hasItems = section.items.length > 0;
          const showEmpty = section.showEmpty !== false;

          if (!hasItems && !showEmpty) return null;

          const sectionAddHandler = section.onAdd ?? (showAddButton ? onAdd : undefined);

          return (
            <div key={section.label} className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold">{section.label}</p>
                {sectionAddHandler && (
                  <button
                    type="button"
                    onClick={sectionAddHandler}
                    className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                )}
              </div>
              {hasItems ? (
                section.items.map((item) => {
                  const row = (
                    <AccountRow
                      key={item.id}
                      item={item}
                      subtitle={buildItemSubtitle(item) || undefined}
                      onEdit={setEditItem}
                    />
                  );
                  return renderItem ? renderItem(item, row) : row;
                })
              ) : (
                <p className="text-sm text-tertiary">None</p>
              )}
            </div>
          );
        })}

        {children}
      </div>

      <div className="mt-4 flex flex-col items-center">
        <Button variant="brand" rounded onClick={onComplete} className="w-full max-w-[400px]">
          {buttonLabel} <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {editItem && SUPPORTED_CATEGORIES.has(editItem.category) ? (
        <CategoryEditDialog
          key={editItem.id}
          item={editItem}
          isOpen
          onClose={() => setEditItem(null)}
          updateItem={updateItem}
          isUpdatingItem={isUpdatingItem}
          deleteItem={deleteItem}
          isDeletingItem={isDeletingItem}
        />
      ) : editItem ? (
        <EditEstateItemModal
          key={editItem.id}
          isOpen
          onClose={() => setEditItem(null)}
          estateId={editItem?.estate_id ?? estateId}
          item={editItem}
        />
      ) : null}
    </div>
  );
};
